import { useRef, useState } from 'react';
import cn from 'classnames';

import OPE from 'js/on-page-editing';

import Icon from 'components/icon/Icon';
import Image from 'components/image/Image';
import { imageSizes } from 'components/image/image-sizes';
import LinkList from 'components/link-list/LinkList';
import RichText from 'components/rich-text/RichText';

import { IKvinnehistorieMilestone, IKvinnehistorieMilestone_Labels } from 'source/NFR.types';

interface KvinnehistorieMilestoneProps extends IKvinnehistorieMilestone {
  labels?: IKvinnehistorieMilestone_Labels;
  initiallyOpen?: boolean;
}

const KvinnehistorieMilestone: React.FC<KvinnehistorieMilestoneProps> = ({
  year,
  title,
  anchorId,
  intro,
  body,
  image,
  links,
  hasMore,
  editUrl,
  labels,
  onPageEditing,
  initiallyOpen = false
}) => {
  // Only the event being edited gets property overlays; it stays open so every field is reachable.
  const isEditable = Boolean(onPageEditing);
  const [isOpen, setIsOpen] = useState(initiallyOpen || isEditable);
  const sectionRef = useRef<HTMLElement>(null);
  const detailsId = `${anchorId}-details`;

  const toggle = () => {
    // Collapsing a card scrolled past the top: bring its heading back on screen.
    if (isOpen && sectionRef.current && sectionRef.current.getBoundingClientRect().top < 0) {
      sectionRef.current.scrollIntoView();
    }
    setIsOpen(!isOpen);
  };

  return (
    <section
      className="kvinnehistorie-milestone"
      id={anchorId}
      aria-labelledby={`${anchorId}-heading`}
      ref={sectionRef}
    >
      <h2 className="kvinnehistorie-milestone--heading" id={`${anchorId}-heading`}>
        <span className="kvinnehistorie-milestone--year" {...OPE(onPageEditing?.year)}>
          {year}
        </span>
        {(title || isEditable) && (
          <span className="kvinnehistorie-milestone--title" {...OPE(onPageEditing?.title)}>
            {title}
          </span>
        )}
      </h2>
      <div className="kvinnehistorie-milestone--summary">
        <div className="kvinnehistorie-milestone--text">
          {(intro || isEditable) && (
            <p className="kvinnehistorie-milestone--intro" {...OPE(onPageEditing?.intro)}>
              {intro}
            </p>
          )}
          {links && (
            <>
              <h3 className="kvinnehistorie-milestone--links-heading">{labels?.relatedLinks || 'Relaterte lenker'}</h3>
              <LinkList className="kvinnehistorie-milestone--links" {...links} />
            </>
          )}
        </div>
        {image && (
          <div
            className="kvinnehistorie-milestone--image"
            style={
              image.aspectRatio
                ? ({ '--kvinnehistorie-milestone-image-ratio': image.aspectRatio } as React.CSSProperties)
                : undefined
            }
          >
            <Image
              {...imageSizes.timelineEvent}
              {...image}
              height={image.aspectRatio ? Math.round(imageSizes.timelineEvent.width / image.aspectRatio) : undefined}
              loading="lazy"
            />
          </div>
        )}
      </div>
      {(hasMore || isEditable) && (
        <div id={detailsId} className="kvinnehistorie-milestone--details" hidden={!isOpen}>
          {body && (
            <div className="kvinnehistorie-milestone--body">
              <RichText {...body} />
            </div>
          )}
        </div>
      )}
      {hasMore && !isEditable && (
        <button
          type="button"
          className="kvinnehistorie-milestone--toggle"
          aria-expanded={isOpen}
          aria-controls={detailsId}
          onClick={toggle}
        >
          {isOpen ? labels?.showLess || 'Vis mindre' : labels?.showMore || 'Vis mer'}
          <Icon className={cn('kvinnehistorie-milestone--toggle-icon', { '-open': isOpen })} name="chevron-down" />
        </button>
      )}
      {editUrl && (
        <a className="kvinnehistorie-milestone--edit-link" href={editUrl} target="_top">
          {labels?.edit || 'Rediger hendelsen'}
        </a>
      )}
    </section>
  );
};

KvinnehistorieMilestone.displayName = 'KvinnehistorieMilestone';

export default KvinnehistorieMilestone;
